<template>
    <div>
        <div class="container-fluid mt-45">
            <div class="row">
                <div class="col-md-6">
                    <Breadcrumbs />
                </div>

                <div v-if="results.results && results.results.length" class="col-md-6 text-right">
                    <button :disabled="isBusy" @click="downloadResults(false)" class="btn btn-success btn-block-mobile btn-lg lg-shadow mr-2" :class="{disabled : isBusy}">Download CSV</button>
                    <button :disabled="isBusy" @click="downloadResults(true)" class="btn btn-success btn-block-mobile btn-lg lg-shadow" :class="{disabled : isBusy}">Download PDF</button>
                </div>
            </div>

            <Loader v-if="loading" />

            <div v-else class="row mt-4">
                <div class="col">
                    <div class="accordion" role="tablist">
                        <div class="card">
                            <div class="card-header bg-none d-flex align-items-center justify-content-between p-3 p-1" role="tab">
                                <h5 class="mb-0">Search criteria</h5>
                                <b-button v-b-toggle.accordion-1 variant="link" @click="collapsed = !collapsed"><span class="underlined">Hide</span> <img class="accordion-chevron" :class="{collapsed : collapsed}" src="@/assets/images/icons/select-chevron.svg" /></b-button>
                            </div>

                            <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
                                <div class="card-body pt-2">
                                    <div class="row">
                                        <div class="col-xl-11">
                                            <div class="row">
                                                <div class="col-xl-4 col-lg-6">
                                                    <div class="form-group">
                                                        <label>Patient name / {{ patientIdentifierLabel.toLowerCase() }} / test no</label>
                                                        <input type="text" class="form-control" :placeholder="`Search for a patient name, ${patientIdentifierLabel.toLowerCase()} or test no`" v-model="advancedSearchParams.patientText">
                                                    </div>

                                                    <div class="form-group">
                                                        <label>DOB</label>
                                                        <div class="row">
                                                            <div class="col-sm-4">
                                                                <select class="custom-select" v-model="advancedSearchParams.dayOfBirth">
                                                                    <option value="null">Day</option>
                                                                    <option v-for="item in 31" :key="item" :value="item">{{ item }}</option>
                                                                </select>
                                                            </div>

                                                            <div class="col-sm-4 mt-1 mt-md-0">
                                                                <select class="custom-select" v-model="advancedSearchParams.monthOfBirth">
                                                                    <option value="null">Month</option>
                                                                    <option value="1">January</option>
                                                                    <option value="2">February</option>
                                                                    <option value="3">March</option>
                                                                    <option value="4">April</option>
                                                                    <option value="5">May</option>
                                                                    <option value="6">June</option>
                                                                    <option value="7">July</option>
                                                                    <option value="8">August</option>
                                                                    <option value="9">September</option>
                                                                    <option value="10">October</option>
                                                                    <option value="11">November</option>
                                                                    <option value="12">December</option>
                                                                </select>
                                                            </div>

                                                            <div class="col-sm-4 mt-1 mt-md-0">
                                                                <select class="custom-select" v-model="advancedSearchParams.yearOfBirth">
                                                                    <option value="null">Year</option>
                                                                    <option v-for="item in years" :key="item" :value="item">{{ item }}</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="form-group">
                                                        <label>Address</label>
                                                        <input type="text" class="form-control" placeholder="Search for address" v-model="advancedSearchParams.patientAddressText">
                                                    </div>

                                                    <div class="form-group">
                                                        <label>Facility</label>
                                                        <select class="custom-select" v-model="advancedSearchParams.facilityId">
                                                            <option value="">All Facilities</option>
                                                            <option v-for="(facility, index) in allFacilities" :key="index" :value="facility.id">{{ facility.name }}</option>
                                                        </select>
                                                    </div>

                                                    <div class="form-group">
                                                        <label>Health care professional</label>
                                                        <select class="custom-select" v-model="advancedSearchParams.practitionerId">
                                                            <option value="">All HCPs</option>
                                                            <option v-for="(practitioner, index) in allPractitioners" :key="index" :value="practitioner.id">{{ practitioner.name }}</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-xl-4 col-lg-6">
                                                    <div class="form-group">
                                                        <label>Issue method</label>
                                                        <select class="custom-select" v-model="advancedSearchParams.issueMethod">
                                                            <option value="">Any</option>
                                                            <option :value="IssueMethod.IN_PERSON">In person</option>
                                                            <option :value="IssueMethod.POST">Post</option>
                                                            <option :value="IssueMethod.COLLECTION">Collection</option>
                                                        </select>
                                                    </div>

                                                    <div class="form-group">
                                                        <div class="row align-items-end">
                                                            <div class="col-md-5">
                                                                <label>Issue date from</label>
                                                                <b-form-datepicker
                                                                    v-model="advancedSearchParams.issueDateFrom" 
                                                                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                                    :locale="locale"
                                                                    placeholder="Choose"
                                                                    hide-header
                                                                    show-decade-nav
                                                                    no-flip
                                                                >
                                                                </b-form-datepicker>
                                                            </div>

                                                            <div class="col-md-5 mt-2 mt-md-0">
                                                                <label>Issue date to</label>
                                                                <b-form-datepicker
                                                                    v-model="advancedSearchParams.issueDateTo" 
                                                                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                                    :locale="locale"
                                                                    placeholder="Choose"
                                                                    hide-header
                                                                    show-decade-nav
                                                                    no-flip
                                                                >
                                                                </b-form-datepicker>
                                                            </div>

                                                            <div class="col-md-2">
                                                                <span @click="clearIssueDates()" class="text-underlined cursor-pointer d-block mb-2">
                                                                    <svg class="clear-date" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 28 28">
                                                                        <g id="Group_654" data-name="Group 654" transform="translate(-1030 -412)">
                                                                            <g class="change-on-hover" id="Ellipse_383" data-name="Ellipse 383" transform="translate(1030 412)" fill="#dae2e7" stroke="#b5c4cf" stroke-width="1">
                                                                            <circle cx="14" cy="14" r="14" stroke="none"/>
                                                                            <circle cx="14" cy="14" r="13.5" fill="none"/>
                                                                            </g>
                                                                            <g id="Group_653" data-name="Group 653">
                                                                            <path id="Path_6099" data-name="Path 6099" d="M14240.6-19010.924l-10.974-10.975,2.121-2.121,10.974,10.975Z" transform="translate(-13192 19443.473)" fill="#515b62"/>
                                                                            <path id="Path_6100" data-name="Path 6100" d="M14241.661-19022.959l-10.974,10.975" transform="translate(-13192.001 19443.473)" fill="none" stroke="#515b62" stroke-width="3"/>
                                                                            </g>
                                                                        </g>
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="form-group">
                                                        <div class="row align-items-end">
                                                            <div class="col-md-5">
                                                                <label>Return date from</label>
                                                                <b-form-datepicker
                                                                    v-model="advancedSearchParams.returnDateFrom" 
                                                                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                                    :locale="locale"
                                                                    placeholder="Choose"
                                                                    hide-header
                                                                    show-decade-nav
                                                                    no-flip
                                                                >
                                                                </b-form-datepicker>
                                                            </div>

                                                            <div class="col-md-5 mt-2 mt-md-0">
                                                                <label>Return date to</label>
                                                                <b-form-datepicker
                                                                    v-model="advancedSearchParams.returnDateTo" 
                                                                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                                    :locale="locale"
                                                                    placeholder="Choose"
                                                                    hide-header
                                                                    show-decade-nav
                                                                    no-flip
                                                                >
                                                                </b-form-datepicker>
                                                            </div>

                                                            <div class="col-md-2">
                                                                <span @click="clearReturnDates()" class="text-underlined cursor-pointer d-block mb-2">
                                                                    <svg class="clear-date" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 28 28">
                                                                        <g id="Group_654" data-name="Group 654" transform="translate(-1030 -412)">
                                                                            <g class="change-on-hover" id="Ellipse_383" data-name="Ellipse 383" transform="translate(1030 412)" fill="#dae2e7" stroke="#b5c4cf" stroke-width="1">
                                                                            <circle cx="14" cy="14" r="14" stroke="none"/>
                                                                            <circle cx="14" cy="14" r="13.5" fill="none"/>
                                                                            </g>
                                                                            <g id="Group_653" data-name="Group 653">
                                                                            <path id="Path_6099" data-name="Path 6099" d="M14240.6-19010.924l-10.974-10.975,2.121-2.121,10.974,10.975Z" transform="translate(-13192 19443.473)" fill="#515b62"/>
                                                                            <path id="Path_6100" data-name="Path 6100" d="M14241.661-19022.959l-10.974,10.975" transform="translate(-13192.001 19443.473)" fill="none" stroke="#515b62" stroke-width="3"/>
                                                                            </g>
                                                                        </g>
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="form-group">
                                                        <div class="row align-items-end">
                                                            <div class="col-md-5">
                                                                <label>Test date from</label>
                                                                <b-form-datepicker
                                                                    v-model="advancedSearchParams.dueDateFrom" 
                                                                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                                    :locale="locale"
                                                                    placeholder="Choose"
                                                                    hide-header
                                                                    show-decade-nav
                                                                    no-flip
                                                                >
                                                                </b-form-datepicker>
                                                            </div>

                                                            <div class="col-md-5 mt-2 mt-md-0">
                                                                <label>Test date to</label>
                                                                <b-form-datepicker
                                                                    v-model="advancedSearchParams.dueDateTo" 
                                                                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                                    :locale="locale"
                                                                    placeholder="Choose"
                                                                    hide-header
                                                                    show-decade-nav
                                                                    no-flip
                                                                >
                                                                </b-form-datepicker>
                                                            </div>

                                                            <div class="col-md-2">
                                                                <span @click="clearDueDates()" class="text-underlined cursor-pointer d-block mb-2">
                                                                    <svg class="clear-date" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 28 28">
                                                                        <g id="Group_654" data-name="Group 654" transform="translate(-1030 -412)">
                                                                            <g class="change-on-hover" id="Ellipse_383" data-name="Ellipse 383" transform="translate(1030 412)" fill="#dae2e7" stroke="#b5c4cf" stroke-width="1">
                                                                            <circle cx="14" cy="14" r="14" stroke="none"/>
                                                                            <circle cx="14" cy="14" r="13.5" fill="none"/>
                                                                            </g>
                                                                            <g id="Group_653" data-name="Group 653">
                                                                            <path id="Path_6099" data-name="Path 6099" d="M14240.6-19010.924l-10.974-10.975,2.121-2.121,10.974,10.975Z" transform="translate(-13192 19443.473)" fill="#515b62"/>
                                                                            <path id="Path_6100" data-name="Path 6100" d="M14241.661-19022.959l-10.974,10.975" transform="translate(-13192.001 19443.473)" fill="none" stroke="#515b62" stroke-width="3"/>
                                                                            </g>
                                                                        </g>
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="form-group">
                                                        <label>Testing for</label>
                                                        <select class="custom-select" v-model="advancedSearchParams.issueReason">
                                                            <option value="">Any</option>
                                                            <option :value="TestIssueReason.GD">Gestational Diabetes Mellitus</option>
                                                            <option :value="TestIssueReason.CF">Cystic Fibrosis</option>
                                                            <option :value="TestIssueReason.TYPE1">Type 1</option>
                                                            <option :value="TestIssueReason.TYPE2">Type 2</option>
                                                            <option :value="TestIssueReason.RESEARCH_STUDY">Research Study</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                
                                                <div class="col-xl-4 col-lg-6 offset-lg-6 offset-xl-0">
                                                    <div class="form-group">
                                                        <label>Threshold (mmol/L)</label>
                                                        <div class="row align-items-end">
                                                            <div class="col-sm-5">
                                                                <label>Min</label>
                                                                <div class="input-group">
                                                                    <input v-model="advancedSearchParams.thresholdMin" type="text" class="form-control" />
                                                                    <div class="input-group-append">
                                                                        <span class="input-group-text">mmol/L</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div class="col-sm-5">
                                                                <label>Max</label>
                                                                <div class="input-group">
                                                                    <input v-model="advancedSearchParams.thresholdMax" type="text" class="form-control" />
                                                                    <div class="input-group-append">
                                                                        <span class="input-group-text">mmol/L</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div class="col-sm-2">
                                                                <span @click="clearThresholds()" class="text-underlined cursor-pointer d-block mb-2">
                                                                    <svg class="clear-date" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 28 28">
                                                                        <g id="Group_654" data-name="Group 654" transform="translate(-1030 -412)">
                                                                            <g class="change-on-hover" id="Ellipse_383" data-name="Ellipse 383" transform="translate(1030 412)" fill="#dae2e7" stroke="#b5c4cf" stroke-width="1">
                                                                            <circle cx="14" cy="14" r="14" stroke="none"/>
                                                                            <circle cx="14" cy="14" r="13.5" fill="none"/>
                                                                            </g>
                                                                            <g id="Group_653" data-name="Group 653">
                                                                            <path id="Path_6099" data-name="Path 6099" d="M14240.6-19010.924l-10.974-10.975,2.121-2.121,10.974,10.975Z" transform="translate(-13192 19443.473)" fill="#515b62"/>
                                                                            <path id="Path_6100" data-name="Path 6100" d="M14241.661-19022.959l-10.974,10.975" transform="translate(-13192.001 19443.473)" fill="none" stroke="#515b62" stroke-width="3"/>
                                                                            </g>
                                                                        </g>
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="form-group">
                                                        <label>Status</label>
                                                        <select class="custom-select" v-model="advancedSearchParams.testStatus">
                                                            <option value="">Any</option>
                                                            <option :value="TestStatus.NEW">New</option>
                                                            <option :value="TestStatus.ISSUED">Issued</option>
                                                            <option :value="TestStatus.RETURNED">Returned</option>
                                                            <option :value="TestStatus.COMPLETE">Complete</option>
                                                            <option :value="TestStatus.CANCELLED">Cancelled</option>
                                                            <option :value="TestStatus.REPORTED">Reported</option>
                                                            <option :value="TestStatus.FAILED">Failed</option>
                                                            <option :value="TestStatus.ERROR">Errored</option>
                                                        </select>
                                                    </div>

                                                    <div class="d-flex mt-4">
                                                        <button :disabled="isBusy" @click="cancel()" onclick="this.blur();" class="btn btn-cancel btn-lg d-flex flex-grow-1 justify-content-center lg-shadow" :class="{disabled : isBusy}">Clear</button>
                                                        <button :disabled="isBusy" @click="search()" onclick="this.blur();" class="btn btn-success btn-lg d-flex flex-grow-1 justify-content-center lg-shadow ml-4" :class="{disabled : isBusy}">Search</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="container-fluid mt-4">
            <div v-if="!results.results.length && advancedSearchParams.searchHasBeenExecuted">
                <div class="row">
                    <div class="col">
                        <div class="mt-4">
                            <div class="alert alert-info">
                                Your search query returned no results. Please try amending your search criteria
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="results.results.length">
                <div class="row mt-5">
                    <div class="col">
                        <p class="h4">Results <small class="font-italic text-sm">{{ `Showing ${showingComputed} of ${results.totalCount}` }}</small></p>
                    </div>
                </div>

                <div class="row mt-4">
                    <div class="col">
                        <b-table 
                            ref="table"
                            id="table"
                            :sort-by.sync="sortBySync" 
                            :sort-desc.sync="sortDesc" 
                            @sort-changed="sortingChanged" 
                            :no-local-sorting="true"
                            :busy.sync="isBusy"
                            class="custom-table bg-white" 
                            responsive
                            no-sort-reset
                            :fields="filteredTestsFields" 
                            :items="results.results"
                        >
                            <template #cell(testNumber)="data">
                                <router-link :to="{name: 'ViewTest', params: {id : data.item.id}}" class="font-weight-bold underlined text-capitalize">{{ data.item.testNumber }}</router-link>
                                <div class="test-history-icons" v-if="data.item.replacedByTestId">
                                    <img src="@/assets/images/icons/reorder-old-icon.svg" v-b-tooltip.hover title="This test has been reordered"/>
                                </div>
                                <div class="test-history-icons" v-else-if="data.item.replacesTestId">
                                    <img src="@/assets/images/icons/reorder-new-icon.svg" v-b-tooltip.hover title="This test replaces a previous test"/>
                                </div>
                                <div class="test-history-icons" v-else-if="data.item.hasBeenPreviouslyReordered">
                                    <img src="@/assets/images/icons/reorder-old-icon.svg" v-b-tooltip.hover title="Test has been previously reordered"/>
                                </div>
                            </template>
                            
                            <template #cell(patientName)="data">
                                <router-link class="font-weight-bold underlined text-capitalize" :to="{name: 'ViewPatient', params: {id : data.item.patientId}}">{{ data.item.patientName }}</router-link>
                            </template>

                            <template v-if="isTrial" #cell(deviceNumber)="data">
                                <span v-if="data.item.deviceNumber">{{ data.item.deviceNumber }}</span>
                                <span v-else>N/A</span>
                            </template>

                            <template #cell(patientPostalCode)="data">
                                <span v-if="data.item.patientPostalCode" class="text-uppercase">{{ data.item.patientPostalCode }}</span>
                                <span v-else-if="data.item.shippingAddress && data.item.shippingAddress.city" class="text-uppercase">{{ data.item.shippingAddress.city }}</span>
                                <span v-else-if="data.item.shippingAddress && data.item.shippingAddress.country" class="text-uppercase">{{ data.item.shippingAddress.provinceName }}</span> 
                                <span v-else class="text-uppercase">N/A</span>
                            </template>

                            <template #cell(status)="data">
                               <testStatuses :status="data.item.status" />
                            </template>

                            <template #cell(action)="data">
                                <router-link :to="{name: 'ViewTest', params: {id : data.item.id}}" class="btn btn-primary sm-shadow text-nowrap">View Test</router-link>
                            </template>
                        </b-table>

                        <div class="mt-4 d-flex flex-column flex-md-row align-items-center justify-content-end">
                            <div class="form-group d-flex align-items-center mb-4 mb-md-0">
                                <label class="flex-shrink-0 mr-2">Items per page</label>
                                <select class="custom-select" v-model="advancedSearchParams.pageSize" @change="handleChangePageSize()">
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                </select>
                            </div>

                            <div>
                                <b-pagination
                                    v-model="currentPage"
                                    :current-page="currentPage"
                                    :per-page="results.pageSize"
                                    :total-rows="results.totalCount"
                                    prev-text="< Previous"
                                    next-text="Next >"
                                    @change="pageChange"
                                    aria-controls="table"
                                    hide-goto-end-buttons
                                    prev-class="prev-page"
                                    next-class="next-page"
                                    align="right"
                                    class="mb-0"
                                ></b-pagination>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapActions, mapGetters} from 'vuex'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import Loader from '@/partials/Loader'
import TestStatuses from '../../../Common/components/TestStatusesComponent.vue'
import IssueMethod from '../../../Common/enums/IssueMethod'
import TestIssueReason from '../../../Common/enums/TestIssueReason'
import TestStatus from '../../../Common/enums/TestStatus'
import SearchResultsMixin from '@/mixins/SearchResultsMixin'
import TotalResultsMixin from '../../../Common/mixins/TotalResultsMixin'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'

export default {
    name: 'AdvancedSearch',

    components: {
        Loader,
        Breadcrumbs,
        TestStatuses
    }, 

    mixins: [
        FormatterMixin,
        SearchResultsMixin,
        TotalResultsMixin
    ],

    data() {
        return {
            loading: false,
            collapsed: false,
            locale: 'en-GB',
            facilityIdData: "",
            userIdData: "",
            IssueMethod,
            TestIssueReason,
            TestStatus,
        }
    },

    async mounted() {
        if(!this.allPractitioners.length || !this.allFacilities.length) {
            this.loading = true
            try {
                await this.getAllPractitioners()
                await this.getAllFacilities()
            }
            catch {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Something went wrong. Please try again.'
                })
            }
            finally {
                this.loading = false
            }
        }
    },

    computed: {
        ...mapGetters({
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel',
            allFacilities: 'FacilityStore/GetAllFacilities',
            allPractitioners: 'PractitionerStore/GetAllPractitioners',
            userId: 'UserStore/GetUser',
            advancedSearchParams: 'SearchStore/GetAdvancedSearchParams',
            patientText: 'SearchStore/GetPatientText',
        }),

        years() {
            var currentYear = new Date().getFullYear(), 
            years = [], 
            startYear = 1920; 

            while ( startYear <= currentYear ) {
                years.push(startYear++);
            }   

            return years;
        }
    },

    methods: {
        ...mapActions({
            getAllFacilities: 'FacilityStore/GetAllFacilities',
            getAllPractitioners: 'PractitionerStore/GetAllPractitioners',
            setSearchResultsInSearchStore: 'SearchStore/SetSearchResults',
            setNotification: 'NotificationStore/SetNotification',
            clearReturnDates: 'SearchStore/ClearReturnDates',
            clearDueDates: 'SearchStore/ClearDueDates',
            clearThresholds: 'SearchStore/ClearThresholds',
            clearSearchStore: 'SearchStore/ClearSearchStore'
        }),

        async search () {
            this.currentPage = 1
            this.offset = 0
            await this.getResults()
        },

        async getResults() {
            let dobString = null

            if(this.advancedSearchParams.dayOfBirth && this.advancedSearchParams.monthOfBirth && this.advancedSearchParams.yearOfBirth) {
                let dob = new Date(Date.UTC(this.advancedSearchParams.yearOfBirth, (this.advancedSearchParams.monthOfBirth - 1), this.advancedSearchParams.dayOfBirth, 0 ,0 ,0, 0))
                dobString = dob.toUTCString()
            }

            let chosenStatusArray = []
            if(this.advancedSearchParams.testStatus !== null && this.advancedSearchParams.testStatus !== "") {
                chosenStatusArray.push(this.advancedSearchParams.testStatus)
            } 

            let searchModel = {
                organisationId: this.organisationId,
                facilityId: this.advancedSearchParams.facilityId,
                practitionerId: this.advancedSearchParams.practitionerId,
                patientText: this.advancedSearchParams.patientText,
                patientDOB: dobString,
                patientAddressText: this.advancedSearchParams.patientAddressText,
                patientId: this.advancedSearchParams.patientId,
                issueDateFrom: this.advancedSearchParams.issueDateFrom,
                issueDateTo: this.advancedSearchParams.issueDateTo,
                returnDateFrom: this.advancedSearchParams.returnDateFrom,
                returnDateTo: this.advancedSearchParams.returnDateTo,
                dueDateFrom: this.advancedSearchParams.dueDateFrom,
                dueDateTo: this.advancedSearchParams.dueDateTo,
                issueMethod: this.advancedSearchParams.issueMethod,
                issueReason: this.advancedSearchParams.issueReason,
                thresholdMin: parseFloat(this.advancedSearchParams.thresholdMin),
                thresholdMax: parseFloat(this.advancedSearchParams.thresholdMax),
                testStatus: chosenStatusArray,
                pageSize: this.advancedSearchParams.pageSize,
                offset: this.offset,
                sortColumn: this.sortColumn,
                sortType: this.sortType
            }

            try {
                this.isBusy = true
                let response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search`, searchModel)

                this.advancedSearchParams.searchHasBeenExecuted = true
                this.setSearchResultsInSearchStore(response.data)
            }
            catch {
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Something went wrong. Please try again.'
                })
            }
            finally {
                this.isBusy = false
            }
        },

        async downloadResults (asPDF) {
            this.isBusy = true 

            let dobString = null

            if(this.advancedSearchParams.dayOfBirth && this.advancedSearchParams.monthOfBirth && this.advancedSearchParams.yearOfBirth) {
                let dob = new Date(Date.UTC(this.advancedSearchParams.yearOfBirth, (this.advancedSearchParams.monthOfBirth - 1), this.advancedSearchParams.dayOfBirth, 0 ,0 ,0, 0))
                dobString = dob.toUTCString()
            }

            let chosenStatusArray = []
            if(this.advancedSearchParams.testStatus !== null && this.advancedSearchParams.testStatus !== "") {
                chosenStatusArray.push(this.advancedSearchParams.testStatus)
            } 

            let searchModel = {
                organisationId: this.organisationId,
                facilityId: this.advancedSearchParams.facilityId,
                practitionerId: this.advancedSearchParams.practitionerId,
                patientText: this.advancedSearchParams.patientText,
                patientDOB: dobString,
                patientAddressText: this.advancedSearchParams.patientAddressText,
                patientId: this.advancedSearchParams.patientId,
                issueDateFrom: this.advancedSearchParams.issueDateFrom,
                issueDateTo: this.advancedSearchParams.issueDateTo,
                returnDateFrom: this.advancedSearchParams.returnDateFrom,
                returnDateTo: this.advancedSearchParams.returnDateTo,
                dueDateFrom: this.advancedSearchParams.dueDateFrom,
                dueDateTo: this.advancedSearchParams.dueDateTo,
                issueMethod: this.advancedSearchParams.issueMethod,
                issueReason: this.advancedSearchParams.issueReason,
                thresholdMin: parseFloat(this.advancedSearchParams.thresholdMin),
                thresholdMax: parseFloat(this.advancedSearchParams.thresholdMax),
                testStatus: chosenStatusArray,
                pageSize: this.advancedSearchParams.pageSize,
                offset: this.offset,
                sortColumn: this.sortColumn,
                sortType: this.sortType
            }

            try {
                const date = new Date()
                var downloadUrl = asPDF ? `${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search/pdf` : `${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/search/csv`
                const response = await axios.post(downloadUrl, searchModel, {
                    responseType: 'blob' // Set the responseType to 'blob'
                })
                const url = asPDF ? window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })) : window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                if (asPDF) {
                    link.setAttribute('download', `Test_Data_${date.toLocaleDateString()}.pdf`)
                } else {
                    link.setAttribute('download', `Test_Data_${date.toLocaleDateString()}.csv`)
                }
                document.body.appendChild(link)
                link.click()
                link.remove()
            }
            catch {
                this.isBusy = false

                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Something went wrong. Please try again.'
                })
            }
            finally {
                this.isBusy = false
            }
        },

        pageChange (value) {
            this.currentPage = value
            this.offset = (value - 1) * this.results.pageSize
            this.getResults()
        },

        clearIssueDates() {
            this.advancedSearchParams.issueDateFrom = null
            this.advancedSearchParams.issueDateTo = null
        },

        clearReturnDates() {
            this.advancedSearchParams.returnDateFrom = null
            this.advancedSearchParams.returnDateTo = null
        },

        clearDueDates() {
            this.advancedSearchParams.dueDateFrom = null
            this.advancedSearchParams.dueDateTo = null
        },

        clearThresholds() {
            this.advancedSearchParams.thresholdMin = null
            this.advancedSearchParams.thresholdMax = null
        },

        cancel() {
            this.clearSearchStore()
        },

        handleChangePageSize () {
            this.currentPage = 1
            this.offset = 0

            this.getResults()
        }
    }
}
</script>